// The brand's Instagram accounts (D-078): the main account, which collects the brand's own
// posts, tags and mentions, and on Pro and Agency extra accounts that only search hashtags.
// Each account can search 30 hashtags a week, so each extra account adds 30.
import type { InstagramStatus } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { ApiError, api, del, post } from "../api";
import { useToast } from "./Toast";

const problems: Record<string, string> = {
  same_account:
    "That's already your main Instagram account. Pick a different account for hashtag searches.",
  no_business_account:
    "We couldn't find an Instagram business or creator account linked to a Facebook Page. Switch the account to a professional account and link it to a Page, then try again.",
  cancelled: "Adding the account was cancelled. Nothing was changed.",
  wrong_user: "You were logged out while connecting. Log in again and try once more.",
  failed:
    "Instagram didn't let us connect. Try again, and check that you are an admin of the Facebook Page.",
};

function Picture({ src }: { src: string | null }) {
  return src ? (
    <img src={src} alt="" width={36} height={36} style={{ borderRadius: "50%" }} />
  ) : (
    <div className="av" aria-hidden="true" />
  );
}

export function InstagramAccounts({ canEdit }: { canEdit: boolean }) {
  const { ws } = useParams();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ig = useQuery({
    queryKey: ["instagram", ws],
    queryFn: () => api<InstagramStatus>(`/v1/w/${ws}/instagram`),
  });
  const choose = params.get("choose");
  const choices = useQuery({
    queryKey: ["ig-choices", choose],
    queryFn: () =>
      api<{
        extra: boolean;
        accounts: { igUserId: string; username: string; pageName: string }[];
      }>(`/v1/instagram/choices/${choose}`),
    enabled: Boolean(choose),
  });

  // Back from Facebook: say how it went once, then tidy the address.
  const problem = params.get("error");
  const connected = params.get("connected");
  useEffect(() => {
    if (!problem && !connected) return;
    if (problem) setError(problems[problem] ?? problems.failed!);
    if (connected) toast("The Instagram account is added. Its hashtags are searched in a moment.");
    setParams(
      (p) => {
        p.delete("error");
        p.delete("connected");
        return p;
      },
      { replace: true },
    );
  }, [problem, connected, setParams, toast]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };
  const connect = (extra: boolean) =>
    act(async () => {
      const { url } = await post<{ url: string }>(`/v1/w/${ws}/instagram/connect`, { extra });
      window.location.href = url;
    });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["instagram", ws] });
    qc.invalidateQueries({ queryKey: ["sources", ws] });
  };

  const s = ig.data;
  if (!s) return null;
  const main = s.accounts.find((a) => a.role === "main");
  const extras = s.accounts.filter((a) => a.role === "extra");

  return (
    <section className="card stack">
      <div>
        <h2>Instagram accounts</h2>
        <p className="muted">
          Your main account collects your own posts and the posts that tag or mention you.
          {s.extraAllowed
            ? " Instagram lets each account search 30 hashtags a week, so add more accounts to follow more hashtags."
            : ""}
        </p>
      </div>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      {choose && choices.data && (
        <div className="stack">
          <h3>Which account should search hashtags?</h3>
          {choices.data.accounts.map((a) => (
            <button
              key={a.igUserId}
              type="button"
              className="btn secondary block"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  await post("/v1/instagram/choose", { state: choose, igUserId: a.igUserId });
                  setParams({}, { replace: true });
                  refresh();
                  toast(`@${a.username} is added. Its hashtags are searched in a moment.`);
                })
              }
            >
              @{a.username} <span className="muted small">({a.pageName})</span>
            </button>
          ))}
        </div>
      )}

      {main ? (
        <div className="row wrap">
          <Picture src={main.profilePictureUrl} />
          <div className="spacer">
            <b>@{main.username}</b> <span className="small muted">Main account</span>
            <div className="small muted">
              {main.status === "connected"
                ? `Connected${main.hashtags.length ? ` · searches ${main.hashtags.map((t) => `#${t}`).join(", ")}` : ""}`
                : ""}
            </div>
            {main.status === "broken" && (
              <div className="small" style={{ color: "var(--warn)" }}>
                {main.lastError ?? "Instagram stopped sending us your posts. Connect again."}
              </div>
            )}
          </div>
          {canEdit && (
            <>
              <button
                type="button"
                className={main.status === "broken" ? "btn sm" : "btn secondary sm"}
                disabled={busy}
                onClick={() => connect(false)}
              >
                Reconnect
              </button>
              <button
                type="button"
                className="btn secondary sm"
                disabled={busy}
                onClick={() => {
                  if (
                    !window.confirm(
                      `Disconnect @${main.username}? Your walls keep the posts they have, but no new posts come in until you connect Instagram again.`,
                    )
                  )
                    return;
                  act(async () => {
                    const r = await del<{ summary: string }>(`/v1/w/${ws}/instagram`);
                    refresh();
                    toast(r.summary);
                  });
                }}
              >
                Disconnect
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="row">
          <span className="spacer muted">No Instagram account is connected.</span>
          {canEdit && (
            <Link to={`/w/${ws}/connect`} className="btn sm">
              Connect Instagram
            </Link>
          )}
        </div>
      )}

      {extras.map((a) => (
        <div key={a.id} className="row wrap">
          <Picture src={a.profilePictureUrl} />
          <div className="spacer">
            <b>@{a.username}</b> <span className="small muted">For hashtags</span>
            <div className="small muted">
              {a.hashtags.length
                ? `Searches ${a.hashtags.map((t) => `#${t}`).join(", ")}`
                : "No hashtags yet. New hashtags go to the account with the most room."}
            </div>
            {(a.status === "broken" || a.lastError) && (
              <div className="small" style={{ color: "var(--warn)" }}>
                {a.lastError ??
                  "Instagram stopped letting Brandwall use this account. Connect it again."}
              </div>
            )}
            {!s.extraAllowed && (
              <div className="small muted">
                Not used now: more accounts for hashtags are on the Pro plan.
              </div>
            )}
          </div>
          {canEdit && (
            <>
              {a.status === "broken" && s.extraAllowed && (
                <button
                  type="button"
                  className="btn sm"
                  disabled={busy}
                  onClick={() => connect(true)}
                >
                  Reconnect
                </button>
              )}
              <button
                type="button"
                className="btn secondary sm"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm(`Remove @${a.username} from hashtag searches?`)) return;
                  act(async () => {
                    const r = await del<{ summary: string }>(
                      `/v1/w/${ws}/instagram/accounts/${a.id}`,
                    );
                    refresh();
                    toast(r.summary);
                  });
                }}
              >
                Remove
              </button>
            </>
          )}
        </div>
      ))}

      {canEdit &&
        main &&
        (s.extraAllowed ? (
          <div className="stack" style={{ gap: 6 }}>
            <button
              type="button"
              className="btn secondary"
              style={{ alignSelf: "start" }}
              disabled={busy}
              onClick={() => connect(true)}
            >
              Add an account for hashtag searches
            </button>
            <p className="small muted">
              Use another Instagram business or creator account you manage, for example a second
              brand account. It only searches hashtags: it never posts, comments or collects its own
              posts.
            </p>
          </div>
        ) : (
          <p className="small muted">
            Need more than 30 hashtags? On Pro you can add more Instagram accounts, each with 30
            hashtags a week. <Link to={`/w/${ws}/settings?tab=plan`}>See plans</Link>
          </p>
        ))}
    </section>
  );
}
